import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormControl,
  Checkbox,
  FormGroup,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Edit, FileDownload } from '@mui/icons-material';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import axios from 'axios';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const convertSnakeToCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => convertSnakeToCamelCase(item));
  const newObj = {};
  for (const snake in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, snake)) {
      const camel = snake.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
      newObj[camel] = convertSnakeToCamelCase(obj[snake]);
    }
  }
  return newObj;
};

const convertCamelToSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => convertCamelToSnakeCase(item));
  const newObj = {};
  for (const camel in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, camel)) {
      const snake = camel.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      newObj[snake] = convertCamelToSnakeCase(obj[camel]);
    }
  }
  return newObj;
};

// ─── Shared input style ───────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '13px',
    '& fieldset': { borderColor: '#d1d5db' },
    '&:hover fieldset': { borderColor: '#2563eb' },
    '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: '13px', '&.Mui-focused': { color: '#2563eb' } },
  '& .MuiFormHelperText-root': { fontSize: '11px', mt: 0.25 },
};
const radioSx = { color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } };
const checkSx = { p: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } };

const SectionLabel = ({ text }) => (
  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1.5, mt: 0.5 }}>
    {text}
  </Typography>
);

// ─── Component ────────────────────────────────────────────────────────────────
const OfficialsProfile = () => {
  const [data, setData] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [muniCodeToNameMap, setMuniCodeToNameMap] = useState({});
  const [postAdminCodeToNameMap, setPostAdminCodeToNameMap] = useState({});
  const [sucoCodeToNameMap, setSucoCodeToNameMap] = useState({});

  const [, setDisplayPostAdminsInModal] = useState([]);
  const [, setDisplaySucosInModal] = useState([]);

  const availableDepartments = useMemo(() => ["GCF", "Ministry of Health", "Ministry of Education", "Ministry of Finance", "National Police (PNTL)"], []);
  const availableDesignations = useMemo(() => ["Officer", "Assistant", "Manager", "Director", "Specialist", "Analyst"], []);
  const availableEmploymentTypes = useMemo(() => ["Permanent", "Contract", "Intern"], []);
  const availablePlatforms = useMemo(() => ["Email", "WhatsApp"], []);
  const availableModules = useMemo(() => ["Cyclone", "Landslide", "Earthquake", "Flood Risk", "Fire Risk", "Lightning", "Road Safety Condition"], []);
  const ageCategories = useMemo(() => ["Below 18", "Between 19-49", "More 50"], []);
  const disabilityOptions = useMemo(() => ["Seeing", "Listening", "Walking", "Speaking", "Others"], []);
  const availableMunicipalities = useMemo(() => ["Dili", "Liquiçá", "Baucau", "Ermera", "Bobonaro", "Covalima", "Manatuto", "Aileu", "Manufahi", "Lautém", "Viqueque", "Ainaro", "Oecusse"], []);

  const [allMunicipalities, setAllMunicipalities] = useState([]);
  const [allPostAdmins, setAllPostAdmins] = useState([]);
  const [allSucos, setAllSucos] = useState([]);

  useEffect(() => {
    const fetchOfficials = async () => {
      try {
        const response = await axios.get(`${backendUrl}dissemination/officials/`);
        const convertedData = convertSnakeToCamelCase(response.data);
        const processedData = convertedData.map(item => ({
          ...item,
          preferredPlatform: item.preferredPlatform ? item.preferredPlatform.split(',') : [],
          modules: Array.isArray(item.modules) ? item.modules : [],
          disabilityTypes: Array.isArray(item.disabilityTypes) ? item.disabilityTypes : [],
        }));
        setData(processedData);
      } catch (err) {
        console.error("Error fetching officials data:", err);
        setError("Failed to fetch data from the server.");
      } finally {
        setLoading(false);
      }
    };

    const fetchGeoData = async () => {
      try {
        const [muniRes, paRes, sucoRes] = await Promise.all([
          axios.post(`${backendUrl}Municipalities/municipalities_list/`),
          axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`),
          axios.post(`${backendUrl}Municipalities/suco_list/`)
        ]);
        const muniMap = {};
        const muniList = muniRes.data.map(m => ({ code: m.adm1_code, name: m.adm1 }));
        muniList.forEach(item => muniMap[item.code] = item.name);
        setMuniCodeToNameMap(muniMap);
        setAllMunicipalities(muniList);

        const paMap = {};
        const paList = paRes.data.map(pa => ({ code: pa.adm2_code, name: pa.adm2, parent_adm1_code: pa.adm1_code }));
        paList.forEach(item => paMap[item.code] = item.name);
        setPostAdminCodeToNameMap(paMap);
        setAllPostAdmins(paList);

        const sucoMap = {};
        const sucoList = sucoRes.data.map(s => ({ code: s.adm3_code, name: s.adm3, parent_adm2_code: s.adm2_code }));
        sucoList.forEach(item => sucoMap[item.code] = item.name);
        setSucoCodeToNameMap(sucoMap);
        setAllSucos(sucoList);
      } catch (err) {
        console.error("Error fetching geographical data:", err);
      }
    };

    fetchOfficials();
    fetchGeoData();
  }, []);

  const handleEditClick = (row) => {
    setEditingRow({
      ...row,
      disabilityTypes: [...(row.disabilityTypes || [])],
      modules: [...(row.modules || [])],
      preferredPlatform: [...(row.preferredPlatform || [])],
      municipality: Array.isArray(row.municipality) ? row.municipality : [],
      postAdmin: Array.isArray(row.postAdmin) ? row.postAdmin : [],
      suco: Array.isArray(row.suco) ? row.suco : [],
    });
    setSaveError('');
    if (Array.isArray(row.municipality)) setDisplayPostAdminsInModal(allPostAdmins.filter(pa => row.municipality.includes(pa.parent_adm1_code)));
    if (Array.isArray(row.postAdmin)) setDisplaySucosInModal(allSucos.filter(s => row.postAdmin.includes(s.parent_adm2_code)));
    setEditDialogOpen(true);
  };

  const handleChange = (field, value) => {
    setEditingRow(prev => {
      let next = { ...prev, [field]: value };
      if (field === "hasDisability" && value === "No") next = { ...next, disabilityTypes: [] };
      if (field === "level") {
        if (value === "Country") next = { ...next, municipality: [], postAdmin: [], suco: [] };
        else if (value === "Municipality") next = { ...next, postAdmin: [], suco: [] };
        else if (value === "Post Admin") next = { ...next, suco: [] };
      }
      return next;
    });
  };

  const handleCheckboxMultiSelectChange = (field, code, isChecked) => {
    setEditingRow(prev => {
      const current = prev[field] || [];
      let newSel = isChecked ? [...current, code] : current.filter(i => i !== code);
      let updated = { ...prev, [field]: newSel };
      if (field === "municipality") {
        const filteredPAs = allPostAdmins.filter(pa => newSel.includes(pa.parent_adm1_code));
        updated = { ...updated, postAdmin: updated.postAdmin.filter(c => filteredPAs.some(pa => pa.code === c)), suco: [] };
        setDisplayPostAdminsInModal(filteredPAs);
        setDisplaySucosInModal([]);
      }
      if (field === "postAdmin") {
        const filteredSucos = allSucos.filter(s => newSel.includes(s.parent_adm2_code));
        updated = { ...updated, suco: updated.suco.filter(c => filteredSucos.some(s => s.code === c)) };
        setDisplaySucosInModal(filteredSucos);
      }
      return updated;
    });
  };

  const handleDisabilityTypeChange = (type, isChecked) => {
    setEditingRow(prev => ({
      ...prev,
      disabilityTypes: isChecked ? [...(prev.disabilityTypes || []), type] : (prev.disabilityTypes || []).filter(t => t !== type),
    }));
  };

  const handlePreferredPlatformChange = (platform, isChecked) => {
    setEditingRow(prev => ({
      ...prev,
      preferredPlatform: isChecked ? [...(prev.preferredPlatform || []), platform] : (prev.preferredPlatform || []).filter(p => p !== platform),
    }));
  };

  const handleModulesChange = (moduleName, isChecked) => {
    setEditingRow(prev => ({
      ...prev,
      modules: isChecked ? [...(prev.modules || []), moduleName] : (prev.modules || []).filter(m => m !== moduleName),
    }));
  };

  const handleSave = async () => {
    if (!editingRow || !editingRow.id) return;
    setSaveError('');

    // Basic validation
    const mobileRegex = /^(77|78|73|74|75|76|70|71|72)\d{6}$/;
    const emailRegex = /^[^\s@]+@([^\s@]+\.)+[^\s@]{2,}$/;
    if (editingRow.email && !emailRegex.test(editingRow.email)) { setSaveError("Invalid email format."); return; }
    if (editingRow.mobileNumber && !mobileRegex.test(editingRow.mobileNumber.replace(/[\s-]/g, ''))) { setSaveError("Invalid mobile number (8 digits starting with 7X)."); return; }
    if (editingRow.whatsappNumber && !mobileRegex.test(editingRow.whatsappNumber.replace(/[\s-]/g, ''))) { setSaveError("Invalid WhatsApp number (8 digits starting with 7X)."); return; }
    if (editingRow.hasDisability === 'Yes' && (!editingRow.disabilityTypes || editingRow.disabilityTypes.length === 0)) { setSaveError("Select at least one disability type."); return; }

    setSaveLoading(true);
    const payload = convertCamelToSnakeCase({ ...editingRow, preferredPlatform: editingRow.preferredPlatform.join(',') });
    try {
      const response = await axios.patch(`${backendUrl}dissemination/officials/${editingRow.id}/`, payload);
      setData(prev => prev.map(row => row.id === editingRow.id ? convertSnakeToCamelCase(response.data) : row));
      setEditDialogOpen(false);
    } catch (err) {
      console.error("Error saving changes:", err.response ? err.response.data : err.message);
      setSaveError("Failed to save changes. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: 'id', header: 'ID', size: 60, enableColumnFilter: false },
      {
        accessorKey: 'fullName', header: 'Full Name', size: 160,
        Cell: ({ cell }) => <span style={{ fontWeight: 600, color: '#1e293b' }}>{cell.getValue()}</span>,
      },
      { accessorKey: 'gender', header: 'Gender', size: 100, filterVariant: 'select' },
      { accessorKey: 'ageCategory', header: 'Age Category', size: 130, filterVariant: 'select', filterSelectOptions: ageCategories },
      {
        accessorKey: 'hasDisability', header: 'Disability', size: 100, filterVariant: 'select', filterSelectOptions: ["No", "Yes"],
        Cell: ({ cell }) => (
          <Chip label={cell.getValue()} size="small" sx={{
            fontSize: '11px', fontWeight: 600,
            background: cell.getValue() === 'Yes' ? '#fef2f2' : '#f0fdf4',
            color: cell.getValue() === 'Yes' ? '#dc2626' : '#16a34a',
            border: `1px solid ${cell.getValue() === 'Yes' ? '#fecaca' : '#bbf7d0'}`,
          }} />
        ),
      },
      {
        accessorKey: 'disabilityTypes', header: 'Disability Types', enableColumnFilter: false,
        Cell: ({ cell }) => <span style={{ color: '#64748b', fontSize: '12px' }}>{Array.isArray(cell.getValue()) ? cell.getValue().join(', ') : ''}</span>,
      },
      {
        accessorKey: 'department', header: 'Department', filterVariant: 'select', filterSelectOptions: availableDepartments,
        Cell: ({ cell }) => <span style={{ color: '#475569' }}>{cell.getValue()}</span>,
      },
      { accessorKey: 'postion', header: 'Designation', filterVariant: 'select', filterSelectOptions: availableDesignations },
      { accessorKey: 'level', header: 'Level', size: 120, filterVariant: 'select', filterSelectOptions: ["Country", "Municipality", "Post Admin", "Suco"] },
      { accessorKey: 'employmentType', header: 'Employment Type', filterVariant: 'select', filterSelectOptions: availableEmploymentTypes },
      {
        accessorKey: 'municipality', header: 'Municipality', filterVariant: 'select', filterSelectOptions: availableMunicipalities,
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return <span style={{ color: '#64748b', fontSize: '12px' }}>{Array.isArray(v) ? v.map(c => muniCodeToNameMap[c] || c).join(', ') : muniCodeToNameMap[v] || v}</span>;
        },
      },
      {
        accessorKey: 'postAdmin', header: 'Post Admin', filterVariant: 'select', filterSelectOptions: Object.values(allPostAdmins).flat(),
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return <span style={{ color: '#64748b', fontSize: '12px' }}>{Array.isArray(v) ? v.map(c => postAdminCodeToNameMap[c] || c).join(', ') : postAdminCodeToNameMap[v] || v}</span>;
        },
      },
      {
        accessorKey: 'suco', header: 'Suco', filterVariant: 'select', filterSelectOptions: Object.values(allSucos).flat(),
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return <span style={{ color: '#64748b', fontSize: '12px' }}>{Array.isArray(v) ? v.map(c => sucoCodeToNameMap[c] || c).join(', ') : sucoCodeToNameMap[v] || v}</span>;
        },
      },
      {
        accessorKey: 'modules', header: 'Interested Modules', enableColumnFilter: false,
        Cell: ({ cell }) => <span style={{ color: '#64748b', fontSize: '12px' }}>{Array.isArray(cell.getValue()) ? cell.getValue().join(', ') : ''}</span>,
      },
      { accessorKey: 'mobileNumber', header: 'Mobile (TL)' },
      { accessorKey: 'whatsappNumber', header: 'WhatsApp (TL)', enableColumnFilter: false },
      {
        accessorKey: 'email', header: 'Email',
        Cell: ({ cell }) => <span style={{ color: '#475569' }}>{cell.getValue()}</span>,
      },
      { accessorKey: 'retirementDate', header: 'Retirement Date', enableColumnFilter: false },
      {
        accessorKey: 'preferredPlatform', header: 'Preferred Platform', filterVariant: 'select', filterSelectOptions: availablePlatforms,
        Cell: ({ cell }) => <span style={{ color: '#64748b', fontSize: '12px' }}>{Array.isArray(cell.getValue()) ? cell.getValue().join(', ') : ''}</span>,
      },
    ],
    [availableDepartments, availableDesignations, availableEmploymentTypes, ageCategories, availableMunicipalities, allPostAdmins, allSucos, availablePlatforms, muniCodeToNameMap, postAdminCodeToNameMap, sucoCodeToNameMap],
  );

  const csvConfig = mkConfig({
    fieldSeparator: ',', decimalSeparator: '.', useKeysAsHeaders: true,
    columnHeaders: [
      { key: 'id', header: 'ID' }, { key: 'fullName', header: 'Full Name' },
      { key: 'gender', header: 'Gender' }, { key: 'ageCategory', header: 'Age Category' },
      { key: 'hasDisability', header: 'Disability Status' }, { key: 'disabilityTypes', header: 'Disability Types' },
      { key: 'department', header: 'Department' }, { key: 'postion', header: 'Designation' },
      { key: 'level', header: 'Level' }, { key: 'employmentType', header: 'Employment Type' },
      { key: 'municipality', header: 'Municipality', format: (v) => Array.isArray(v) ? v.map(c => muniCodeToNameMap[c] || c).join(', ') : muniCodeToNameMap[v] || v },
      { key: 'postAdmin', header: 'Post Admin', format: (v) => Array.isArray(v) ? v.map(c => postAdminCodeToNameMap[c] || c).join(', ') : postAdminCodeToNameMap[v] || v },
      { key: 'suco', header: 'Suco', format: (v) => Array.isArray(v) ? v.map(c => sucoCodeToNameMap[c] || c).join(', ') : sucoCodeToNameMap[v] || v },
      { key: 'modules', header: 'Modules' }, { key: 'mobileNumber', header: 'Mobile' },
      { key: 'whatsappNumber', header: 'WhatsApp' }, { key: 'email', header: 'Email' },
      { key: 'retirementDate', header: 'Retirement Date' }, { key: 'preferredPlatform', header: 'Preferred Platform' },
    ],
  });

  const handleExportData = () => {
    const dataToExport = data.map(row => ({
      ...row,
      disabilityTypes: Array.isArray(row.disabilityTypes) ? row.disabilityTypes.join(', ') : '',
      modules: Array.isArray(row.modules) ? row.modules.join(', ') : '',
      preferredPlatform: Array.isArray(row.preferredPlatform) ? row.preferredPlatform.join(',') : '',
    }));
    download(csvConfig)(generateCsv(csvConfig)(dataToExport));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 6, gap: 2 }}>
        <CircularProgress size={24} sx={{ color: '#2563eb' }} />
        <Typography sx={{ color: '#64748b', fontSize: '14px' }}>Loading official profiles...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
      <MaterialReactTable
        columns={columns}
        data={data}
        enableColumnFilterModes={false}
        enableColumnOrdering={false}
        enableColumnPinning={false}
        enableFacetedValues
        enableGrouping={false} 
        enablePagination
        enableSorting
        enableColumnActions={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableRowActions
        positionActionsColumn="last"
        initialState={{ density: "comfortable", pagination: { pageSize: 15, pageIndex: 0 } }}
        renderRowActions={({ row }) => (
          <Tooltip title="Edit Profile" arrow>
            <IconButton
              size="small"
              onClick={() => handleEditClick(row.original)}
              sx={{ color: '#2563eb', '&:hover': { backgroundColor: '#2563eb14' } }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: '4px' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
              Officials Profile
            </Typography>
            <Chip
              label={`${data.length} Records`}
              size="small"
              sx={{ background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '12px', border: '1px solid #e2e8f0' }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownload fontSize="small" />}
              onClick={handleExportData}
              sx={{ fontSize: '12px', textTransform: 'none', borderColor: '#2563eb', color: '#2563eb', borderRadius: '7px', '&:hover': { background: '#eff6ff' } }}
            >
              Export CSV
            </Button>
          </Box>
        )}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 240px)', borderRadius: '12px' } }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 700, backgroundColor: '#f8fafc', color: '#475569',
            fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px',
            borderBottom: '2px solid #e2e8f0',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '13px', borderBottom: '1px solid #f1f5f9',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          },
        }}
        muiTableBodyRowProps={{
          sx: { '&:hover': { backgroundColor: '#f8fafc' }, transition: 'background-color 0.15s ease' },
        }}
        muiTopToolbarProps={{ sx: { backgroundColor: 'transparent' } }}
        muiBottomToolbarProps={{ sx: { backgroundColor: 'transparent', borderTop: '1px solid #e2e8f0' } }}
      />

      {/* ── Edit Dialog ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !saveLoading && setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '17px', color: '#1e293b', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", pb: 0.5 }}>
          Edit Official Profile
          {editingRow && (
            <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 400, mt: 0.25 }}>
              {editingRow.fullName}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2, fontSize: '13px', borderRadius: '8px' }}>{saveError}</Alert>}

          {editingRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* Personal */}
              <Box>
                <SectionLabel text="Personal Information" />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField label="Full Name" value={editingRow.fullName || ''} onChange={(e) => handleChange("fullName", e.target.value)} size="small" fullWidth sx={inputSx} />
                  <TextField label="Gender" value={editingRow.gender || ''} onChange={(e) => handleChange("gender", e.target.value)} select size="small" fullWidth sx={inputSx}>
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 0.5 }}>Age Category</FormLabel>
                    <RadioGroup row value={editingRow.ageCategory || ''} onChange={(e) => handleChange("ageCategory", e.target.value)}>
                      {ageCategories.map(cat => (
                        <FormControlLabel key={cat} value={cat} control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '13px' }}>{cat}</Typography>} sx={{ mr: 1.5 }} />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormControl>
                    <FormLabel sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 0.5 }}>Person with Disabilities?</FormLabel>
                    <RadioGroup row value={editingRow.hasDisability || ''} onChange={(e) => handleChange("hasDisability", e.target.value)}>
                      <FormControlLabel value="No" control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '13px' }}>No</Typography>} sx={{ mr: 2 }} />
                      <FormControlLabel value="Yes" control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '13px' }}>Yes</Typography>} />
                    </RadioGroup>
                    {editingRow.hasDisability === 'Yes' && (
                      <FormGroup row sx={{ mt: 0.5 }}>
                        {disabilityOptions.map(type => (
                          <FormControlLabel key={type} control={<Checkbox size="small" checked={(editingRow.disabilityTypes || []).includes(type)} onChange={(e) => handleDisabilityTypeChange(type, e.target.checked)} sx={checkSx} />} label={<Typography sx={{ fontSize: '13px' }}>{type}</Typography>} sx={{ mr: 1 }} />
                        ))}
                      </FormGroup>
                    )}
                  </FormControl>
                </Box>
              </Box>

              <Divider sx={{ borderColor: '#e2e8f0' }} />

              {/* Work */}
              <Box>
                <SectionLabel text="Work Information" />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField label="Department" value={editingRow.department || ''} onChange={(e) => handleChange("department", e.target.value)} select size="small" fullWidth sx={inputSx}>
                    <MenuItem value="">Select Department</MenuItem>
                    {availableDepartments.map((d, i) => <MenuItem key={i} value={d}>{d}</MenuItem>)}
                  </TextField>
                  <TextField label="Designation" value={editingRow.postion || ''} onChange={(e) => handleChange("postion", e.target.value)} select size="small" fullWidth sx={inputSx}>
                    <MenuItem value="">Select Designation</MenuItem>
                    {availableDesignations.map((d, i) => <MenuItem key={i} value={d}>{d}</MenuItem>)}
                  </TextField>
                  <TextField label="Level" value={editingRow.level || ''} onChange={(e) => handleChange("level", e.target.value)} select size="small" fullWidth sx={inputSx}>
                    <MenuItem value="">Select Level</MenuItem>
                    <MenuItem value="Country">Country</MenuItem>
                    <MenuItem value="Municipality">Municipality</MenuItem>
                    <MenuItem value="Post Admin">Post Admin</MenuItem>
                    <MenuItem value="Suco">Suco</MenuItem>
                  </TextField>
                  <TextField label="Employment Type" value={editingRow.employmentType || ''} onChange={(e) => handleChange("employmentType", e.target.value)} select size="small" fullWidth sx={inputSx}>
                    <MenuItem value="">Select Employment Type</MenuItem>
                    {availableEmploymentTypes.map((t, i) => <MenuItem key={i} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Box>
              </Box>

              {/* Geographic */}
              {(editingRow.level === "Municipality" || editingRow.level === "Post Admin" || editingRow.level === "Suco") && (
                <>
                  <Divider sx={{ borderColor: '#e2e8f0' }} />
                  <Box>
                    <SectionLabel text="Geographic Scope" />
                    <Box sx={{ display: 'grid', gridTemplateColumns: editingRow.level === "Suco" ? '1fr 1fr 1fr' : editingRow.level === "Post Admin" ? '1fr 1fr' : '1fr', gap: 2 }}>
                      {/* Municipality */}
                      <Box>
                        <FormLabel sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 0.5, display: 'block' }}>Municipality</FormLabel>
                        <Box sx={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', p: 1 }}>
                          {allMunicipalities.map(muni => (
                            <FormControlLabel key={muni.code} control={<Checkbox size="small" checked={(editingRow.municipality || []).includes(muni.code)} onChange={(e) => handleCheckboxMultiSelectChange("municipality", muni.code, e.target.checked)} sx={checkSx} />} label={<Typography sx={{ fontSize: '13px' }}>{muni.name}</Typography>} sx={{ display: 'flex', m: 0, py: 0.25 }} />
                          ))}
                        </Box>
                      </Box>

                      {/* Post Admin */}
                      {(editingRow.level === "Post Admin" || editingRow.level === "Suco") && (
                        <Box>
                          <FormLabel sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 0.5, display: 'block' }}>Post Admin</FormLabel>
                          <Box sx={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', p: 1 }}>
                            {allPostAdmins.filter(pa => (editingRow.municipality || []).includes(pa.parent_adm1_code)).map(post => (
                              <FormControlLabel key={post.code} control={<Checkbox size="small" checked={(editingRow.postAdmin || []).includes(post.code)} onChange={(e) => handleCheckboxMultiSelectChange("postAdmin", post.code, e.target.checked)} sx={checkSx} />} label={<Typography sx={{ fontSize: '13px' }}>{post.name}</Typography>} sx={{ display: 'flex', m: 0, py: 0.25 }} />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Suco */}
                      {editingRow.level === "Suco" && (
                        <Box>
                          <FormLabel sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 0.5, display: 'block' }}>Suco</FormLabel>
                          <Box sx={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', p: 1 }}>
                            {allSucos.filter(s => (editingRow.postAdmin || []).includes(s.parent_adm2_code)).map(suco => (
                              <FormControlLabel key={suco.code} control={<Checkbox size="small" checked={(editingRow.suco || []).includes(suco.code)} onChange={(e) => handleCheckboxMultiSelectChange("suco", suco.code, e.target.checked)} sx={checkSx} />} label={<Typography sx={{ fontSize: '13px' }}>{suco.name}</Typography>} sx={{ display: 'flex', m: 0, py: 0.25 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              <Divider sx={{ borderColor: '#e2e8f0' }} />

              {/* Contact */}
              <Box>
                <SectionLabel text="Contact & Alert Preferences" />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField label="Mobile Number (TL)" value={editingRow.mobileNumber || ''} onChange={(e) => handleChange("mobileNumber", e.target.value)} size="small" fullWidth sx={inputSx} placeholder="e.g., 77XXXXXXX" />
                  <TextField label="WhatsApp Number (TL)" value={editingRow.whatsappNumber || ''} onChange={(e) => handleChange("whatsappNumber", e.target.value)} size="small" fullWidth sx={inputSx} placeholder="e.g., 77XXXXXXX" />
                  <TextField label="Official Email" type="email" value={editingRow.email || ''} onChange={(e) => handleChange("email", e.target.value)} size="small" fullWidth sx={inputSx} />
                  <TextField label="Retirement Date" type="date" value={editingRow.retirementDate || ''} onChange={(e) => handleChange("retirementDate", e.target.value)} size="small" fullWidth sx={inputSx} InputLabelProps={{ shrink: true }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 0.5 }}>Preferred Platform</FormLabel>
                    <FormGroup row>
                      {availablePlatforms.map(p => (
                        <FormControlLabel key={p} control={<Checkbox size="small" checked={(editingRow.preferredPlatform || []).includes(p)} onChange={(e) => handlePreferredPlatformChange(p, e.target.checked)} sx={checkSx} />} label={<Typography sx={{ fontSize: '13px' }}>{p}</Typography>} sx={{ mr: 2 }} />
                      ))}
                    </FormGroup>
                  </FormControl>
                  <FormControl>
                    <FormLabel sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 0.5 }}>Interested Modules</FormLabel>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {availableModules.map(mod => (
                        <FormControlLabel key={mod} control={<Checkbox size="small" checked={(editingRow.modules || []).includes(mod)} onChange={(e) => handleModulesChange(mod, e.target.checked)} sx={checkSx} />} label={<Typography sx={{ fontSize: '12px' }}>{mod}</Typography>} sx={{ mr: 1, mb: 0 }} />
                      ))}
                    </Box>
                  </FormControl>
                </Box>
              </Box>

            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={saveLoading}
            sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600, fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saveLoading}
            sx={{
              backgroundColor: '#2563eb', textTransform: 'none', fontWeight: 600,
              borderRadius: '8px', px: 3, minWidth: '120px', boxShadow: 'none',
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              '&:hover': { backgroundColor: '#1d4ed8' },
            }}
          >
            {saveLoading ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfficialsProfile;


